// Master Seed Catalog for Database Initialization
// Defines the 31 earnable badges:
// 1 Universal ("First on the Scene")
// 15 Attending Badges (1st, 5th, 10th for each of the 5 event types)
// 15 Hosting Badges (1st, 5th, 10th for each of the 5 event types)

import type { BadgeDefinition } from './types';

export const initialBadges: BadgeDefinition[] = [
  // --- UNIVERSAL ---
  {
    id: "b1",
    name: "First on the Scene",
    category: "Universal",
    actionType: "first_rsvp",
    threshold: 1,
    icon: "fa-bolt",
    desc: "First dad to RSVP and enlist in a new listing across any event type."
  },

  // --- PROJECTS (Attend: 1, 5, 10 | Host: 1, 5, 10) ---
  {
    id: "proj_att_1",
    name: "Toolbelt Novice",
    category: "Projects",
    eventType: "project",
    actionType: "attend",
    threshold: 1,
    icon: "fa-wrench",
    desc: "Showed up and lent a capable pair of hands to your 1st hands-on project."
  },
  {
    id: "proj_att_5",
    name: "Master of the Framing",
    category: "Projects",
    eventType: "project",
    actionType: "attend",
    threshold: 5,
    icon: "fa-hammer",
    desc: "Stepped up on the jobsite across 5 physical construction or repair builds."
  },
  {
    id: "proj_att_10",
    name: "Jobsite Veteran",
    category: "Projects",
    eventType: "project",
    actionType: "attend",
    threshold: 10,
    icon: "fa-screwdriver-wrench",
    desc: "Reliable bedrock of the neighborhood crew with 10 completed builds."
  },
  {
    id: "proj_host_1",
    name: "Blueprint Initiator",
    category: "Projects",
    eventType: "project",
    actionType: "host",
    threshold: 1,
    icon: "fa-clipboard-check",
    desc: "Rallied neighbor dads by hosting and leading your 1st project mission."
  },
  {
    id: "proj_host_5",
    name: "General Contractor",
    category: "Projects",
    eventType: "project",
    actionType: "host",
    threshold: 5,
    icon: "fa-trowel-bricks",
    desc: "Proven jobsite coordinator with 5 successfully hosted build missions."
  },
  {
    id: "proj_host_10",
    name: "Master Builder",
    category: "Projects",
    eventType: "project",
    actionType: "host",
    threshold: 10,
    icon: "fa-helmet-safety",
    desc: "Top-tier neighborhood craftsman who has rallied and hosted 10 community builds."
  },

  // --- SOCIALS (Attend: 1, 5, 10 | Host: 1, 5, 10) ---
  {
    id: "soc_att_1",
    name: "Backyard Guest",
    category: "Socials",
    eventType: "social",
    actionType: "attend",
    threshold: 1,
    icon: "fa-chair",
    desc: "Pulled up a lawn chair and broke the ice at your 1st neighborhood social."
  },
  {
    id: "soc_att_5",
    name: "Firepit Regular",
    category: "Socials",
    eventType: "social",
    actionType: "attend",
    threshold: 5,
    icon: "fa-fire",
    desc: "A familiar friendly face around the smoker and campfire across 5 gatherings."
  },
  {
    id: "soc_att_10",
    name: "Neighborhood Fixture",
    category: "Socials",
    eventType: "social",
    actionType: "attend",
    threshold: 10,
    icon: "fa-beer-mug-empty",
    desc: "Cornerstone of neighborhood camaraderie with 10 attended socials."
  },
  {
    id: "soc_host_1",
    name: "Grill Starter",
    category: "Socials",
    eventType: "social",
    actionType: "host",
    threshold: 1,
    icon: "fa-utensils",
    desc: "Fired up the grill and brought the neighborhood together for your 1st hosted social."
  },
  {
    id: "soc_host_5",
    name: "Pitmaster General",
    category: "Socials",
    eventType: "social",
    actionType: "host",
    threshold: 5,
    icon: "fa-fire-burner",
    desc: "Master of low-and-slow hospitality with 5 hosted backyard gatherings."
  },
  {
    id: "soc_host_10",
    name: "Legendary Host",
    category: "Socials",
    eventType: "social",
    actionType: "host",
    threshold: 10,
    icon: "fa-crown",
    desc: "Ultimate neighborhood connector who has hosted 10 epic dad socials."
  },

  // --- VOLUNTEERING (Attend: 1, 5, 10 | Host: 1, 5, 10) ---
  {
    id: "vol_att_1",
    name: "Helping Hand",
    category: "Volunteering",
    eventType: "volunteer",
    actionType: "attend",
    threshold: 1,
    icon: "fa-hand-holding-heart",
    desc: "Lent your time and effort to your 1st community volunteer mission."
  },
  {
    id: "vol_att_5",
    name: "Civic Father",
    category: "Volunteering",
    eventType: "volunteer",
    actionType: "attend",
    threshold: 5,
    icon: "fa-handshake-angle",
    desc: "Dedicated to the broader community with 5 completed volunteer service events."
  },
  {
    id: "vol_att_10",
    name: "Community Pillar",
    category: "Volunteering",
    eventType: "volunteer",
    actionType: "attend",
    threshold: 10,
    icon: "fa-monument",
    desc: "Outstanding selfless contributor with 10 community and school service missions."
  },
  {
    id: "vol_host_1",
    name: "Civic Initiator",
    category: "Volunteering",
    eventType: "volunteer",
    actionType: "host",
    threshold: 1,
    icon: "fa-bullhorn",
    desc: "Identified a community need and rallied fellow dads for your 1st hosted service project."
  },
  {
    id: "vol_host_5",
    name: "Community Organizer",
    category: "Volunteering",
    eventType: "volunteer",
    actionType: "host",
    threshold: 5,
    icon: "fa-people-group",
    desc: "Spearheaded 5 impactful community cleanup or civic improvement initiatives."
  },
  {
    id: "vol_host_10",
    name: "Community Champion",
    category: "Volunteering",
    eventType: "volunteer",
    actionType: "host",
    threshold: 10,
    icon: "fa-award",
    desc: "Visionary civic leader who has organized 10 impactful community service events."
  },

  // --- SPORTS (Attend: 1, 5, 10 | Host: 1, 5, 10) ---
  {
    id: "spo_att_1",
    name: "Laced Up",
    category: "Sports",
    eventType: "sports",
    actionType: "attend",
    threshold: 1,
    icon: "fa-shoe-prints",
    desc: "Hit the court, diamond, or field for your 1st neighborhood sports match."
  },
  {
    id: "spo_att_5",
    name: "Starting Lineup",
    category: "Sports",
    eventType: "sports",
    actionType: "attend",
    threshold: 5,
    icon: "fa-basketball",
    desc: "Consistent playmaker across 5 sports runs and casual pickup games."
  },
  {
    id: "spo_att_10",
    name: "Franchise Veteran",
    category: "Sports",
    eventType: "sports",
    actionType: "attend",
    threshold: 10,
    icon: "fa-trophy",
    desc: "Ironman athlete who has competed in 10 neighborhood sports matchups."
  },
  {
    id: "spo_host_1",
    name: "Game Organizer",
    category: "Sports",
    eventType: "sports",
    actionType: "host",
    threshold: 1,
    icon: "fa-stopwatch",
    desc: "Secured the court and set the rules for your 1st hosted sports matchup."
  },
  {
    id: "spo_host_5",
    name: "League Commissioner",
    category: "Sports",
    eventType: "sports",
    actionType: "host",
    threshold: 5,
    icon: "fa-medal",
    desc: "Kept the competitive spirit alive by hosting 5 active sports events."
  },
  {
    id: "spo_host_10",
    name: "Athletic Director",
    category: "Sports",
    eventType: "sports",
    actionType: "host",
    threshold: 10,
    icon: "fa-flag-checkered",
    desc: "Neighborhood athletic organizer who has hosted 10 community sporting events."
  },

  // --- GAMING (Attend: 1, 5, 10 | Host: 1, 5, 10) ---
  {
    id: "gam_att_1",
    name: "Player Two Ready",
    category: "Gaming",
    eventType: "gaming",
    actionType: "attend",
    threshold: 1,
    icon: "fa-gamepad",
    desc: "Grabbed a controller or rolled the dice at your 1st neighborhood game night."
  },
  {
    id: "gam_att_5",
    name: "High Scorer",
    category: "Gaming",
    eventType: "gaming",
    actionType: "attend",
    threshold: 5,
    icon: "fa-dice-d20",
    desc: "Proven tactician and tabletop regular across 5 gaming sessions."
  },
  {
    id: "gam_att_10",
    name: "Grandmaster",
    category: "Gaming",
    eventType: "gaming",
    actionType: "attend",
    threshold: 10,
    icon: "fa-chess-knight",
    desc: "Master gamer who has thrown down in 10 epic tabletop or arcade game nights."
  },
  {
    id: "gam_host_1",
    name: "Session Host",
    category: "Gaming",
    eventType: "gaming",
    actionType: "host",
    threshold: 1,
    icon: "fa-tv",
    desc: "Opened up your living room or garage setup for your 1st hosted game night."
  },
  {
    id: "gam_host_5",
    name: "Dungeon Master",
    category: "Gaming",
    eventType: "gaming",
    actionType: "host",
    threshold: 5,
    icon: "fa-dice",
    desc: "Creator of epic campaigns and tournaments across 5 hosted game nights."
  },
  {
    id: "gam_host_10",
    name: "Arcade Proprietor",
    category: "Gaming",
    eventType: "gaming",
    actionType: "host",
    threshold: 10,
    icon: "fa-headset",
    desc: "Legendary gaming hub host who has organized 10 neighborhood game nights."
  }
];
